import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { getCategoryById, getItemById } from '../data/companyData';
import { ArrowLeft, MessageSquare, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const ItemDetailPage = () => {
  const { categoryId, itemId } = useParams();
  const category = getCategoryById(categoryId);
  const item = getItemById(categoryId, itemId);
  const [selectedImage, setSelectedImage] = useState(0);

  if (!category || !item) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Item tidak ditemukan</h1>
          <Link to="/produk-layanan" className="text-[#001582] hover:underline mt-4 inline-block">
            Kembali ke Produk & Layanan
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Get all images (gallery if exists, otherwise just main image)
  const images = item.gallery && item.gallery.length > 0 ? item.gallery : [item.image];

  return (
    <div className="min-h-screen bg-white" data-testid="item-detail-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-[#001582] to-[#001ea7] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFC107] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-white/70 text-sm mb-6">
            <Link to="/produk-layanan" className="hover:text-white transition-colors">
              Produk & Layanan
            </Link>
            <span>/</span>
            <Link to={`/produk-layanan/${category.id}`} className="hover:text-white transition-colors">
              {category.title}
            </Link>
            <span>/</span>
            <span className="text-white">{item.title}</span>
          </div>
          
          <Link 
            to={`/produk-layanan/${category.id}`}
            className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke {category.title}
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
              {category.title}
            </span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white"
            data-testid="item-title"
          >
            {item.title}
          </motion.h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Images */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Main Image */}
              <img 
                src={images[selectedImage]}
                alt={item.title}
                className="w-full aspect-[4/3] object-cover rounded-3xl shadow-xl mb-4"
                data-testid="item-image"
              />
              
              {/* Thumbnail Gallery */}
              {images.length > 1 && (
                <div className="grid grid-cols-3 gap-3">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`relative aspect-[4/3] rounded-xl overflow-hidden transition-all duration-300 ${
                        selectedImage === index 
                          ? 'ring-3 ring-[#FFC107] ring-offset-2' 
                          : 'opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img 
                        src={img}
                        alt={`${item.title} - Foto ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-6">
                Deskripsi
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8" data-testid="item-description">
                {item.fullDescription}
              </p>

              {/* Specs */}
              {item.specs && item.specs.length > 0 && (
                <div className="bg-[#f5f7fb] rounded-2xl p-6 mb-8">
                  <h3 className="font-semibold text-[#1a1a1a] mb-4">Spesifikasi</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {item.specs.map((spec) => (
                      <div key={spec} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#FFC107] flex-shrink-0" />
                        <span className="text-slate-700 text-sm">{spec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Industry Function */}
              {item.industryFunction && (
                <div className="mb-8">
                  <h3 className="font-semibold text-[#1a1a1a] mb-3">Fungsi di Industri</h3>
                  <p className="text-slate-600 leading-relaxed" data-testid="item-function">
                    {item.industryFunction}
                  </p>
                </div>
              )}

              {/* CTA */}
              <a 
                href="https://wa.me/6285210005588" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg"
                  className="w-full bg-[#FFC107] text-[#001582] hover:bg-[#FFD54F] font-bold py-6 rounded-xl"
                  data-testid="cta-whatsapp"
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Hubungi Kami
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Items */}
      {category.items.length > 1 && (
        <section className="py-16 bg-[#f5f7fb]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h3 className="font-heading text-2xl font-bold text-[#1a1a1a] mb-8">
              Item Lain di {category.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {category.items
                .filter(i => i.id !== item.id)
                .slice(0, 3)
                .map((relatedItem) => (
                  <Link 
                    key={relatedItem.id}
                    to={`/produk-layanan/${category.id}/${relatedItem.id}`}
                    className="group"
                  >
                    <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300">
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <img 
                          src={relatedItem.image}
                          alt={relatedItem.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="font-semibold text-[#1a1a1a] group-hover:text-[#001582] transition-colors line-clamp-1">
                          {relatedItem.title}
                        </h4>
                        <p className="text-slate-500 text-sm mt-1 line-clamp-2">
                          {relatedItem.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ItemDetailPage;
